import { booleanExpressionTypes, ExpressionType_ResolutionFunction, numericExpressionTypes, rangeExpressionTypes } from './consts.js';
import { BadImplementationException, capitalizeAllFirstLetters, exists, MUSTNeverHappenException } from '@nu-art/ts-common';
import { BaseDeltaLogic, SelfOperators } from '../../_enum/index.js';
export const createExpressionLabel = (labelList, expressionType, associatedVariable) => {
    const baseLabel = capitalizeAllFirstLetters(`${associatedVariable.name} - ${expressionType}`);
    // In case it's the first state set the first label
    if (!labelList.length)
        return baseLabel;
    // Find the missing index to update the label
    for (let i = 0; i <= labelList.length; i++) {
        const currentLabel = `${baseLabel} (${i + 1})`;
        if (!labelList.includes(currentLabel) && i)
            return currentLabel;
    }
    // In case no index is missing add the list length
    return `${baseLabel} (${labelList.length + 1})`;
};
export const getLeftHandType = (lefthand, mappers, atom) => {
    switch (lefthand.type) {
        case 'atom':
            if (!atom)
                throw new BadImplementationException('cannot resolve lefthand type atom without passing an atom!');
            if (!mappers.valueMap || !mappers.variableMap)
                throw new BadImplementationException('missing mapper');
            return getAtomType(atom, mappers.variableMap, mappers.valueMap);
        case 'attribute':
        case 'variable':
        case 'precipitating':
        case 'relieving': {
            if (!mappers.valueMap || !mappers.variableMap)
                throw new BadImplementationException('missing mapper');
            return getValueType(lefthand, mappers.variableMap, mappers.valueMap);
        }
        case 'expression': {
            if (!mappers.expressionMap)
                throw new BadImplementationException('must get an expression mapper when trying to get expression type');
            return getExpressionType(lefthand.id, mappers.expressionMap);
        }
    }
};
export const getExpressionType = (expressionId, expressionsMap) => {
    const expression = expressionsMap[expressionId];
    if (!expression)
        throw new MUSTNeverHappenException(`Expression with id ${expressionId} not found`);
    // As defined by the AI team the output type of an expression is not selectable but derived from expression type!
    if (booleanExpressionTypes.includes(expression.expressionType))
        return 'boolean';
    if (numericExpressionTypes.includes(expression.expressionType))
        return 'numeric';
    if (rangeExpressionTypes.includes(expression.expressionType))
        return 'range';
    throw new MUSTNeverHappenException(`cannot resolve expression type ${expression.expressionType}`);
};
export const getValueType = (leftHand, variableMap, valueMap) => {
    const variableId = leftHand.propertyId ?? leftHand.id;
    const variable = variableMap[variableId];
    const value = valueMap[variable.valueId];
    if (!value) {
        throw new MUSTNeverHappenException(`could not find value with id ${variable?.valueId}`);
    }
    return value.type;
};
export const getAtomType = (atom, variableMap, valueMap) => {
    // Handle numeric value cases
    if (atom.cases.every(_case => exists(_case.value))) {
        return atom.cases.every(_case => _case.value[0] === _case.value[1]) ? 'numeric' : 'range';
    }
    // Handle self value
    if (SelfOperators.includes(atom.cases[0].caseOperator)) {
        const valueType = getValueType(atom.leftHand, variableMap, valueMap);
        // if it's a delta result treat it like numeric, it ain't normal range
        if (valueType === 'standard-range' && getBaseDeltaOperator(atom.cases[0].caseOperator))
            return 'numeric';
        switch (valueType) {
            case 'enumerated':
                return 'enum';
            case 'tuple':
                return 'boolean';
            case 'standard-range':
                return 'normal-range';
            case 'range':
                return 'numeric';
            case 'time':
                return 'time';
        }
    }
    return 'boolean';
};
export const resolveValueType = (leftHand, mappers) => {
    if (!leftHand)
        throw new MUSTNeverHappenException('cannot pick case operator without left hand');
    const valueType = getLeftHandType(leftHand, mappers);
    if (valueType === 'boolean' || valueType === 'tuple')
        return 'boolean';
    if (valueType === 'numeric' || valueType === 'range' || valueType === 'standard-range')
        return 'numeric';
    return 'enum';
};
export const getBaseDeltaOperator = (caseOperator) => BaseDeltaLogic.find(op => caseOperator?.startsWith(op));
export const getDeltaTypeOperator = (caseOperator) => {
    const baseDelta = getBaseDeltaOperator(caseOperator);
    if (!baseDelta)
        throw new BadImplementationException('cannot get delta type if delta is not in use');
    return caseOperator.replace(baseDelta, '');
};
export const expressionHasVariableValueConnection = (expression, variableId, valueId) => {
    return expression.terms.some(term => {
        term.atoms.some(atom => {
            atom.leftHand?.id === variableId
                && atom.cases.some(_case => {
                    _case.rightHand.includes(valueId);
                });
        });
    });
};
export const isUserPickableExpression = (expression) => {
    return expression.expressionType !== ExpressionType_ResolutionFunction.dbKey
        && !expression.label.startsWith('DPV');
};
//# sourceMappingURL=utils.js.map